import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { useOrdersEnabled } from "@/hooks/useOrdersEnabled";

export default function CartSidebar() {
  const { items, delivery, setDelivery, updateQty, removeItem, totals, open, setOpen, clear, priceOf, isWholesale, wholesaleValidation } = useCart();
  const [step, setStep] = useState("cart"); // cart | details
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", tin: "", delivery_address: "", family_island_name: "" });
  const navigate = useNavigate();
  const ordersEnabled = useOrdersEnabled();

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const goToDetails = () => {
    if (items.length === 0) return;
    setStep("details");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (delivery === "local_delivery" && !form.delivery_address.trim()) {
      toast.error("Please provide a delivery address.");
      return;
    }
    if (delivery === "family_island" && (!form.family_island_name.trim() || !form.delivery_address.trim())) {
      toast.error("Please provide the island and full delivery address.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        tin: form.tin || null,
        delivery_method: delivery,
        delivery_address: (delivery === "local_delivery" || delivery === "family_island") ? form.delivery_address : null,
        family_island_name: delivery === "family_island" ? form.family_island_name : null,
        items: items.map((i) => ({
          slug: i.slug, qty: i.qty,
        })),
      };
      const { data } = await api.post("/order", payload);
      // Clear cart + close sidebar, then navigate to dedicated confirmation page
      clear();
      setOpen(false);
      setStep("cart");
      setForm({ name: "", email: "", phone: "", tin: "", delivery_address: "", family_island_name: "" });
      navigate("/order-confirmation", { state: { order: data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your order. Please try again.");
    } finally { setBusy(false); }
  };

  const closeAndReset = () => {
    setOpen(false);
    setTimeout(() => {
      setStep("cart");
      setForm({ name: "", email: "", phone: "", tin: "", delivery_address: "", family_island_name: "" });
    }, 300);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) closeAndReset(); else setOpen(v); }}>
      <SheetContent showCloseButton={false} data-testid="cart-sidebar" side="right" className="w-full sm:max-w-md p-0 border-l border-brand-border bg-white flex flex-col">
        <SheetTitle className="sr-only">Order Request</SheetTitle>
        <SheetDescription className="sr-only">Review your items, choose fulfilment, and submit an order request. No payment now — bank transfer instructions will be emailed to you.</SheetDescription>
        <div className="flex items-center justify-between px-6 py-5 border-b border-brand-border">
          <div className="flex items-center gap-3">
            <ShoppingBag strokeWidth={1.5} className="w-5 h-5 text-brand-accent" />
            <div>
              <p className="overline">Order Request</p>
              <p className="font-serif text-xl text-brand-dark">
                {step === "details" ? "Your details" : `${totals.count} ${totals.count === 1 ? "item" : "items"}`}
              </p>
            </div>
          </div>
          <button data-testid="cart-close" onClick={closeAndReset} className="text-brand-neutral hover:text-brand-accent">
            <X strokeWidth={1.5} className="w-5 h-5" />
          </button>
        </div>

        {step === "cart" && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <div data-testid="cart-empty" className="text-center py-20">
                  <p className="font-serif italic text-2xl text-brand-dark">Your order is empty.</p>
                  <p className="text-brand-neutral mt-3 text-sm">Add a card or two — we'll take it from here.</p>
                </div>
              ) : (
                <ul className="divide-y divide-brand-border">
                  {items.map((i) => (
                    <li key={i.slug} data-testid={`cart-item-${i.slug}`} className="py-5 flex gap-4">
                      <div className="w-20 h-24 shrink-0 overflow-hidden bg-brand-surface">
                        {i.image && <img src={i.image} alt={i.title} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 flex flex-col">
                        <div className="flex justify-between gap-2">
                          <p className="font-serif text-lg text-brand-dark leading-tight">{i.title}</p>
                          <button data-testid={`cart-remove-${i.slug}`} onClick={() => removeItem(i.slug)} className="text-brand-neutral hover:text-brand-accent shrink-0">
                            <X strokeWidth={1.5} className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-brand-neutral mt-1">
                          {isWholesale && i.wholesale_price != null ? (
                            <>
                              <span className="text-brand-accent font-medium">${i.wholesale_price.toFixed(2)}</span>
                              <span className="ml-2 text-xs text-brand-neutral/70 line-through">${i.price.toFixed(2)}</span>
                            </>
                          ) : (
                            <>${i.price.toFixed(2)}</>
                          )}
                        </p>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center border border-brand-border">
                            <button data-testid={`cart-minus-${i.slug}`} onClick={() => updateQty(i.slug, i.qty - 1)} className="px-2.5 py-1.5 text-brand-neutral hover:text-brand-accent">
                              <Minus strokeWidth={1.5} className="w-3.5 h-3.5" />
                            </button>
                            <span data-testid={`cart-qty-${i.slug}`} className="px-3 text-sm text-brand-dark">{i.qty}</span>
                            <button data-testid={`cart-plus-${i.slug}`} onClick={() => updateQty(i.slug, i.qty + 1)} className="px-2.5 py-1.5 text-brand-neutral hover:text-brand-accent">
                              <Plus strokeWidth={1.5} className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-sm text-brand-dark font-medium">${(priceOf(i) * i.qty).toFixed(2)}</p>
                        </div>
                        {isWholesale && wholesaleValidation.minQty && i.qty < wholesaleValidation.minQty && (
                          <p data-testid={`cart-minqty-warn-${i.slug}`} className="text-[11px] text-brand-accent mt-2">
                            Minimum {wholesaleValidation.minQty} per design.
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-brand-border px-6 py-5 space-y-4">
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between text-brand-dark">
                    <span>Products Subtotal</span>
                    <span data-testid="cart-products-subtotal">${totals.products_subtotal.toFixed(2)}</span>
                  </div>
                  <p className="text-[11px] text-brand-neutral italic">
                    Delivery, VAT and total calculated at the next step.
                  </p>
                </div>

                {isWholesale && !wholesaleValidation.valid && (
                  <div data-testid="wholesale-cart-alert" className="p-4 border border-brand-accent/40 bg-brand-surface">
                    <p className="text-[11px] tracking-widest uppercase text-brand-accent font-medium">Wholesale Order Requirements</p>
                    <ul className="mt-2 space-y-1 text-xs text-brand-neutral leading-relaxed">
                      {wholesaleValidation.issues.map(x => <li key={x.key}>• {x.message}</li>)}
                    </ul>
                    <p className="mt-2 text-xs text-brand-neutral">Please update your cart before submitting your order.</p>
                  </div>
                )}
                {ordersEnabled !== true ? (
                  <div
                    data-testid="orders-disabled-cart"
                    className="border border-brand-border bg-[#F4F2EC] px-5 py-5 text-center"
                  >
                    <p className="text-[11px] tracking-[0.28em] uppercase text-brand-accent font-medium">
                      Order request options coming soon
                    </p>
                    <p className="mt-2 text-xs text-brand-neutral leading-relaxed">
                      Ordering will reopen shortly. You can keep browsing the collection in the meantime.
                    </p>
                  </div>
                ) : (
                  <>
                    <button data-testid="cart-checkout-btn" onClick={goToDetails} disabled={isWholesale && !wholesaleValidation.valid} className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                      Proceed to Order
                    </button>
                    <p className="text-[11px] text-brand-neutral text-center leading-relaxed">
                      This is an order request — no payment now.<br />
                      You'll receive bank transfer instructions via email.
                    </p>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {step === "details" && ordersEnabled === true && (
          <form onSubmit={submit} className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-5">
              <label className="block">
                <span className="overline block mb-1">Full name *</span>
                <input data-testid="order-name" required className="flushed-input" value={form.name} onChange={(e) => setField("name", e.target.value)} />
              </label>
              <label className="block">
                <span className="overline block mb-1">Email *</span>
                <input data-testid="order-email" required type="email" className="flushed-input" value={form.email} onChange={(e) => setField("email", e.target.value)} />
              </label>
              <label className="block">
                <span className="overline block mb-1">Phone (optional)</span>
                <input data-testid="order-phone" className="flushed-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
              </label>
              <label className="block">
                <span className="overline block mb-1">TIN <span className="text-brand-neutral normal-case tracking-normal">(if applicable)</span></span>
                <input data-testid="order-tin" className="flushed-input" value={form.tin} onChange={(e) => setField("tin", e.target.value)} placeholder="Tax Identification Number" />
              </label>

              <div className="pt-4 mt-2 border-t border-brand-border">
                <p className="overline mb-3">Fulfilment</p>
                <div className="space-y-2">
                  <button
                    type="button"
                    data-testid="details-delivery-collection"
                    onClick={() => setDelivery("collection")}
                    className={`w-full p-3 text-left text-sm border ${delivery === "collection" ? "border-brand-accent bg-brand-surface" : "border-brand-border hover:border-brand-accent"}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-brand-dark">Collection</p>
                      <p className="text-xs text-brand-neutral">Free</p>
                    </div>
                    <p className="text-xs text-brand-neutral mt-0.5">Love Beach · by appointment</p>
                  </button>
                  <button
                    type="button"
                    data-testid="details-delivery-local"
                    onClick={() => setDelivery("local_delivery")}
                    className={`w-full p-3 text-left text-sm border ${delivery === "local_delivery" ? "border-brand-accent bg-brand-surface" : "border-brand-border hover:border-brand-accent"}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-brand-dark">Nassau Delivery</p>
                      <p className="text-xs text-brand-neutral">$5.00</p>
                    </div>
                    <p className="text-xs text-brand-neutral mt-0.5">New Providence island</p>
                  </button>
                  <button
                    type="button"
                    data-testid="details-delivery-family"
                    onClick={() => setDelivery("family_island")}
                    className={`w-full p-3 text-left text-sm border ${delivery === "family_island" ? "border-brand-accent bg-brand-surface" : "border-brand-border hover:border-brand-accent"}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-brand-dark">Family Island Delivery</p>
                      <p className="text-xs text-brand-neutral">Quoted</p>
                    </div>
                    <p className="text-xs text-brand-neutral mt-0.5">Price confirmed after order</p>
                  </button>
                </div>
              </div>

              {delivery === "local_delivery" && (
                <label className="block">
                  <span className="overline block mb-1">Delivery address *</span>
                  <textarea data-testid="order-address" required rows={2} className="flushed-input" value={form.delivery_address} onChange={(e) => setField("delivery_address", e.target.value)} placeholder="Street, area, Nassau" />
                </label>
              )}
              {delivery === "family_island" && (
                <>
                  <label className="block">
                    <span className="overline block mb-1">Island *</span>
                    <input data-testid="order-island" required className="flushed-input" value={form.family_island_name} onChange={(e) => setField("family_island_name", e.target.value)} placeholder="e.g. Eleuthera, Exuma, Abaco" />
                  </label>
                  <label className="block">
                    <span className="overline block mb-1">Delivery address *</span>
                    <textarea data-testid="order-address" required rows={2} className="flushed-input" value={form.delivery_address} onChange={(e) => setField("delivery_address", e.target.value)} placeholder="Settlement, street, contact on-island" />
                  </label>
                  <p className="text-xs text-brand-neutral leading-relaxed">
                    We'll email you a Family Island delivery quote based on destination and shipping method before your order ships.
                  </p>
                </>
              )}

              <div className="mt-4 p-4 bg-brand-surface border border-brand-border">
                <p className="overline mb-2">Summary</p>
                <div className="text-sm space-y-1 text-brand-neutral">
                  <div className="flex justify-between"><span>Products Subtotal</span><span>${totals.products_subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between">
                    <span>
                      {delivery === "local_delivery" ? "Delivery · Nassau"
                       : delivery === "family_island" ? "Delivery · Family Island"
                       : "Delivery · Collection · Love Beach"}
                    </span>
                    <span>
                      {delivery === "family_island"
                        ? <em className="not-italic text-brand-accent text-xs">Quoted separately</em>
                        : `$${totals.delivery_fee.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 mt-2 border-t border-brand-border text-brand-dark">
                    <span>Subtotal (before VAT)</span><span>${totals.pre_vat_subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between"><span>VAT (10%)</span><span>${totals.vat.toFixed(2)}</span></div>
                  <div className="flex justify-between pt-2 mt-2 border-t border-brand-border">
                    <span className="text-brand-dark font-medium">
                      {delivery === "family_island" ? "Total Due (excl. delivery)" : "Total Due"}
                    </span>
                    <span className="text-brand-accent font-medium">${totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-brand-border px-6 py-5 space-y-3">
              {isWholesale && !wholesaleValidation.valid && (
                <div data-testid="wholesale-validation-alert" className="p-4 bg-brand-surface border border-brand-accent/40">
                  <p className="text-[11px] tracking-widest uppercase text-brand-accent font-medium">Wholesale Order Requirements</p>
                  <ul className="mt-2 space-y-1 text-xs text-brand-neutral leading-relaxed">
                    {wholesaleValidation.issues.map(i => (
                      <li key={i.key}>• {i.message}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-brand-neutral">Please update your cart before submitting your order.</p>
                </div>
              )}
              <button data-testid="order-submit-btn" type="submit" disabled={busy || (isWholesale && !wholesaleValidation.valid)} className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                {busy ? "Sending…" : "Submit Order Request"}
              </button>
              <button data-testid="order-back-btn" type="button" onClick={() => setStep("cart")} className="w-full text-xs uppercase tracking-widest text-brand-neutral hover:text-brand-accent">
                Back to cart
              </button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
